import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reveal } from "@/components/Reveal";

gsap.registerPlugin(ScrollTrigger);

const SHOTS = [
    {
        src: "/showcase/living.webp",
        title: "Modern Living Room",
        tag: "Full Home Makeover",
        wish: "An open, light-filled living space",
        span: "md:col-span-7",
        h: "h-[300px] sm:h-[380px] lg:h-[440px]",
    },
    {
        src: "/showcase/kitchen.webp",
        title: "Luxury Kitchen",
        tag: "Kitchen Renovation",
        wish: "A kitchen made for everyday moments",
        span: "md:col-span-5",
        h: "h-[300px] sm:h-[380px] lg:h-[440px]",
    },
    {
        src: "/showcase/bathroom.webp",
        title: "Spa Bathroom",
        tag: "Bathroom Renovation",
        wish: "A calm, spa-like retreat",
        span: "md:col-span-4",
        h: "h-[280px] sm:h-[340px] lg:h-[380px]",
    },
    {
        src: "/showcase/bedroom.webp",
        title: "Restful Bedroom",
        tag: "Interior Design",
        wish: "A peaceful space to unwind",
        span: "md:col-span-4",
        h: "h-[280px] sm:h-[340px] lg:h-[380px]",
    },
    {
        src: "/showcase/office.webp",
        title: "Modern Workspace",
        tag: "Commercial Renovation",
        wish: "A workspace built for focus",
        span: "md:col-span-4",
        h: "h-[280px] sm:h-[340px] lg:h-[380px]",
    },
];

const settleCard = (card) => {
    const wish = card.querySelector("[data-showcase-wish]");
    const madeReal = card.querySelector("[data-showcase-made-real]");
    const veil = card.querySelector("[data-showcase-veil]");
    const sweep = card.querySelector("[data-showcase-sweep]");
    const sparkle = card.querySelector("[data-showcase-sparkle]");
    if (veil) gsap.set(veil, { opacity: 0 });
    if (wish) gsap.set(wish, { autoAlpha: 0, y: -10 });
    if (sweep) gsap.set(sweep, { autoAlpha: 0, xPercent: 220 });
    if (sparkle) gsap.set(sparkle, { autoAlpha: 0, scale: 0.8 });
    if (madeReal) gsap.set(madeReal, { autoAlpha: 1, y: 0, height: "auto", overflow: "visible" });
};

export const Showcase = () => {
    const ref = useRef(null);

    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const isMobile = window.matchMedia("(max-width: 767px)").matches;

        const ctx = gsap.context(() => {
            const cards = gsap.utils.toArray("[data-showcase-card]", ref.current);

            if (reduced) {
                cards.forEach((card) => settleCard(card));
                return;
            }

            gsap.utils.toArray(".parallax-inner", ref.current).forEach((inner) => {
                gsap.fromTo(
                    inner,
                    { yPercent: -6 },
                    {
                        yPercent: 6,
                        ease: "none",
                        scrollTrigger: { trigger: inner.parentElement, start: "top bottom", end: "bottom top", scrub: true },
                    },
                );
            });

            const sweepStart = 0.25;
            const sweepDuration = isMobile ? 0.68 : 0.78;
            const sweepPeak = isMobile ? 0.94 : 1;
            const sequenceEnd = 0.95;

            cards.forEach((card) => {
                const wish = card.querySelector("[data-showcase-wish]");
                const madeReal = card.querySelector("[data-showcase-made-real]");
                const veil = card.querySelector("[data-showcase-veil]");
                const sweep = card.querySelector("[data-showcase-sweep]");
                const sparkle = card.querySelector("[data-showcase-sparkle]");
                if (!wish || !madeReal || !veil || !sweep) return;

                const targets = [wish, madeReal, veil, sweep, sparkle].filter(Boolean);

                gsap.set(veil, { opacity: 0.2 });
                gsap.set(wish, { autoAlpha: 1, y: 0 });
                gsap.set(madeReal, { autoAlpha: 0, y: 8, height: 0, overflow: "hidden" });
                gsap.set(sweep, { xPercent: -110, autoAlpha: 0 });
                if (sparkle) gsap.set(sparkle, { scale: 0.7, autoAlpha: 0, transformOrigin: "center center" });

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: card,
                        start: "top 82%",
                        toggleActions: "play none none none",
                        once: true,
                    },
                    onStart: () => {
                        gsap.killTweensOf(targets);
                    },
                    onComplete: () => settleCard(card),
                    onInterrupt: () => settleCard(card),
                });

                tl.to(veil, { opacity: 0, duration: sweepDuration, ease: "power2.inOut" }, sweepStart);

                tl.fromTo(
                    sweep,
                    { xPercent: -110, autoAlpha: 0 },
                    { xPercent: 220, autoAlpha: sweepPeak, duration: sweepDuration, ease: "power2.inOut" },
                    sweepStart,
                ).to(sweep, { autoAlpha: 0, duration: 0.15, ease: "power1.in" }, sequenceEnd - 0.15);

                tl.to(wish, { autoAlpha: 0, y: -10, duration: 0.38, ease: "power2.inOut" }, 0.35);

                tl.to(
                    madeReal,
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.25,
                        ease: "power2.out",
                        onStart: () => {
                            gsap.set(madeReal, { height: "auto", overflow: "visible" });
                        },
                    },
                    0.6,
                );

                if (sparkle) {
                    tl.fromTo(
                        sparkle,
                        { scale: 0.7, autoAlpha: 0 },
                        { scale: 1, autoAlpha: 1, duration: 0.18, ease: "power2.out" },
                        0.6,
                    ).to(sparkle, { scale: 0.8, autoAlpha: 0, duration: 0.17, ease: "power1.in" }, 0.78);
                }
            });
        }, ref);

        return () => ctx.revert();
    }, []);

    return (
        <section id="showcase" ref={ref} data-testid="showcase-section" className="relative overflow-hidden bg-brand-surface py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <Reveal>
                        <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.24em] text-brand-gold">
                            04 — Showcase
                        </p>
                        <h2 className="max-w-xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter text-brand-deep sm:text-5xl">
                            Imagine It. <span className="italic text-brand-teal">We Make It Real.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <p className="max-w-sm text-sm leading-relaxed text-brand-muted sm:text-base">
                            A glimpse of what a wish becomes — real spaces, transformed end to end by Reno Genie
                            professionals.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
                    {SHOTS.map((shot, i) => (
                        <Reveal key={shot.src} delay={0.05 * (i % 3)} className={shot.span}>
                            <figure
                                data-showcase-card
                                data-testid={`showcase-card-${i + 1}`}
                                className={`group relative overflow-hidden rounded-[28px] ${shot.h}`}
                            >
                                <div className="parallax-inner absolute -top-[8%] left-0 h-[116%] w-full will-change-transform">
                                    <img
                                        src={shot.src}
                                        alt={shot.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                                    />
                                </div>
                                <div
                                    data-showcase-veil
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 z-[5] bg-[#F4EDE4] opacity-[0.2]"
                                />
                                <div
                                    data-showcase-sweep
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-y-0 left-0 z-[18] w-[44%] opacity-0 will-change-transform sm:w-[48%]"
                                    style={{
                                        background:
                                            "linear-gradient(90deg, transparent 0%, rgba(253,185,39,0.1) 20%, rgba(253,185,39,0.42) 44%, rgba(253,185,39,0.68) 50%, rgba(253,185,39,0.42) 56%, rgba(253,185,39,0.1) 80%, transparent 100%)",
                                        filter: "blur(6px)",
                                    }}
                                />
                                <div
                                    data-showcase-wish
                                    className="pointer-events-none absolute left-4 top-4 z-10 sm:left-5 sm:top-5"
                                >
                                    <div className="max-w-[10.75rem] rounded-2xl border border-white/15 bg-brand-deep/60 px-3.5 py-3 backdrop-blur-md sm:max-w-[12rem] sm:px-4 sm:py-3.5">
                                        <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-brand-gold sm:text-[10px] sm:tracking-[0.24em]">
                                            <span aria-hidden="true" className="mr-1.5 opacity-90">
                                                ✦
                                            </span>
                                            Your Wish
                                        </p>
                                        <p className="mt-1.5 font-display text-sm italic leading-snug text-white sm:text-[0.9375rem]">
                                            &ldquo;{shot.wish}&rdquo;
                                        </p>
                                    </div>
                                </div>
                                <figcaption className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-brand-deep/80 via-brand-deep/35 to-transparent p-6 pt-16">
                                    <p
                                        data-showcase-made-real
                                        className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-gold opacity-0 h-0 overflow-hidden"
                                    >
                                        <span data-showcase-sparkle aria-hidden="true" className="mr-1.5 inline-block opacity-0">
                                            ✦
                                        </span>
                                        Made Real
                                    </p>
                                    <p className="mt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-gold">
                                        {shot.tag}
                                    </p>
                                    <h3 className="mt-1.5 font-display text-xl italic text-white [text-shadow:0_2px_14px_rgba(2,27,30,0.6)] sm:text-2xl">
                                        {shot.title}
                                    </h3>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
